// Cross-platform CAMERA barcode scanning.
//
// Ported from waio-inventory (src/hooks/useBarcodeScanner.ts) and renamed to
// `useCameraScanner` so it does NOT collide with the POS's existing keyboard-
// wedge `useBarcodeScanner` (hardware scanner that types a code + Enter). This
// is additive: the wedge path is untouched. Logic is otherwise verbatim —
// keep in sync with inventory.
//
//   - Native (Capacitor iOS / Android): uses @capacitor-mlkit/barcode-scanning,
//     which opens its own full-screen camera UI.
//   - Web: dispatches to either STRICH or ZXing-JS based on the build-time
//     VITE_SCANNER_ENGINE flag (see lib/scannerEngine.ts). The caller-supplied
//     container <div> hosts whichever engine's <video> element.
//   - Unsupported (no getUserMedia): callers fall back to manual entry.
//
// All web engines and the MLKit module are loaded lazily so they don't pull
// into the initial bundle / error in the wrong runtime.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { ensureStrichInitialized } from '@/lib/strich'
import { scannerEngine } from '@/lib/scannerEngine'
import type { BarcodeReader } from '@pixelverse/strichjs-sdk'
import type { IScannerControls } from '@zxing/browser'

export type ScannerPlatform = 'native' | 'web' | 'unsupported'

let cachedPlatformPromise: Promise<ScannerPlatform> | null = null

async function determinePlatform(): Promise<ScannerPlatform> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning')
      const { supported } = await BarcodeScanner.isSupported()
      return supported ? 'native' : 'unsupported'
    } catch (e) {
      console.warn('[useCameraScanner] native check failed:', e)
      return 'unsupported'
    }
  }
  if (typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia) {
    return 'web'
  }
  return 'unsupported'
}

export interface UseCameraScannerArgs {
  /** Required for `platform === 'web'`. The hook puts the engine's <video> inside this. */
  containerRef?: React.RefObject<HTMLDivElement | null>
}

export interface UseCameraScanner {
  platform: ScannerPlatform
  /** Convenience flag — `true` when platform is native or web. */
  supported: boolean
  /** True after the platform check resolves. */
  ready: boolean
  /** True while a scan is in progress. */
  scanning: boolean
  /** Run a scan; resolves to the decoded value, or null on cancel/error. */
  scan: () => Promise<string | null>
  /** Cancel an in-flight web scan and tear down the camera. No-op on native. */
  stop: () => void
}

export function useCameraScanner(args: UseCameraScannerArgs = {}): UseCameraScanner {
  const { containerRef } = args

  const [platform, setPlatform] = useState<ScannerPlatform>('unsupported')
  const [ready, setReady] = useState(false)
  const [scanning, setScanning] = useState(false)
  const aliveRef = useRef(true)
  // Single resolver for the in-flight scan promise. Set during scanWeb,
  // cleared on finish. stop() calls it to abort with `null`.
  const finishRef = useRef<((value: string | null) => void) | null>(null)

  useEffect(() => {
    aliveRef.current = true
    if (!cachedPlatformPromise) cachedPlatformPromise = determinePlatform()
    cachedPlatformPromise.then((p) => {
      if (!aliveRef.current) return
      setPlatform(p)
      setReady(true)
    })
    return () => {
      aliveRef.current = false
    }
  }, [])

  const stop = useCallback(() => {
    finishRef.current?.(null)
  }, [])

  // Always stop on unmount so the camera light goes off when the user
  // navigates away from the scan route.
  useEffect(() => {
    return () => stop()
  }, [stop])

  const scanNative = useCallback(async (): Promise<string | null> => {
    try {
      const { BarcodeScanner, BarcodeFormat } = await import(
        '@capacitor-mlkit/barcode-scanning'
      )

      const perm = await BarcodeScanner.requestPermissions()
      if (perm.camera !== 'granted' && perm.camera !== 'limited') return null

      try {
        await BarcodeScanner.installGoogleBarcodeScannerModule()
      } catch {
        /* iOS or already installed */
      }

      const result = await BarcodeScanner.scan({
        formats: [
          BarcodeFormat.Ean13,
          BarcodeFormat.Ean8,
          BarcodeFormat.UpcA,
          BarcodeFormat.UpcE,
          BarcodeFormat.Code128,
          BarcodeFormat.Code39,
          BarcodeFormat.Code93,
          BarcodeFormat.QrCode,
          BarcodeFormat.DataMatrix,
        ],
      })
      return result.barcodes[0]?.rawValue ?? null
    } catch (e) {
      console.error('[useCameraScanner] native scan failed:', e)
      return null
    }
  }, [])

  const scanStrich = useCallback(async (): Promise<string | null> => {
    const container = containerRef?.current
    if (!container) {
      console.warn('[useCameraScanner] no containerRef supplied for STRICH scan')
      return null
    }

    try {
      await ensureStrichInitialized()
      const { BarcodeReader } = await import('@pixelverse/strichjs-sdk')

      return await new Promise<string | null>((resolve) => {
        let settled = false
        let activeReader: BarcodeReader | null = null

        const finish = (value: string | null) => {
          if (settled) return
          settled = true
          finishRef.current = null
          const reader = activeReader
          activeReader = null
          if (reader) void reader.destroy().catch(() => {})
          resolve(value)
        }
        finishRef.current = finish

        const reader = new BarcodeReader({
          selector: container,
          engine: {
            // Narrow list per STRICH support — fewer symbologies = more
            // engine cycles per format, which matters for DataBar Expanded
            // Stacked. UPC/EAN kept in for non-GS1 retail SKUs.
            symbologies: [
              'databar-exp',
              'databar',
              'datamatrix',
              'ean13',
              'upca',
              'code128',
            ],
            duplicateInterval: 2500,
          },
        })

        reader
          .initialize()
          .then((r) => {
            if (settled) return
            activeReader = r
            r.detected = (codes) => {
              if (codes.length > 0) finish(codes[0].data)
            }
            return r.start()
          })
          .catch((e) => {
            console.error('[useCameraScanner] STRICH init/start failed:', e)
            finish(null)
          })
      })
    } catch (e) {
      console.error('[useCameraScanner] STRICH scan failed:', e)
      return null
    }
  }, [containerRef])

  const scanZxing = useCallback(async (): Promise<string | null> => {
    const container = containerRef?.current
    if (!container) {
      console.warn('[useCameraScanner] no containerRef supplied for ZXing scan')
      return null
    }

    // Build a video element inside the container. ZXing wires getUserMedia
    // to it and runs its decode loop against frames captured from it.
    const video = document.createElement('video')
    video.style.width = '100%'
    video.style.height = '100%'
    video.style.objectFit = 'cover'
    video.muted = true
    video.autoplay = true
    video.playsInline = true
    video.setAttribute('playsinline', 'true')
    container.appendChild(video)

    try {
      const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] =
        await Promise.all([import('@zxing/browser'), import('@zxing/library')])

      const hints = new Map()
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.QR_CODE,
        BarcodeFormat.DATA_MATRIX,
        BarcodeFormat.RSS_14,
        BarcodeFormat.RSS_EXPANDED,
      ])
      hints.set(DecodeHintType.TRY_HARDER, true)

      const reader = new BrowserMultiFormatReader(hints)

      return await new Promise<string | null>((resolve) => {
        let settled = false
        let controls: IScannerControls | null = null

        const finish = (value: string | null) => {
          if (settled) return
          settled = true
          finishRef.current = null
          controls?.stop()
          controls = null
          const stream = video.srcObject as MediaStream | null
          stream?.getTracks().forEach((t) => t.stop())
          video.srcObject = null
          video.remove()
          resolve(value)
        }
        finishRef.current = finish

        reader
          .decodeFromVideoDevice(undefined, video, (result) => {
            if (result) finish(result.getText())
          })
          .then((ctrl) => {
            if (settled) {
              ctrl.stop()
              return
            }
            controls = ctrl
          })
          .catch((e) => {
            console.error('[useCameraScanner] ZXing start failed:', e)
            finish(null)
          })
      })
    } catch (e) {
      console.error('[useCameraScanner] ZXing scan failed:', e)
      video.remove()
      return null
    }
  }, [containerRef])

  const scan = useCallback(async (): Promise<string | null> => {
    if (!ready || platform === 'unsupported') return null
    setScanning(true)
    try {
      if (platform === 'native') return await scanNative()
      if (platform === 'web') {
        return scannerEngine === 'strich' ? await scanStrich() : await scanZxing()
      }
      return null
    } finally {
      if (aliveRef.current) setScanning(false)
    }
  }, [ready, platform, scanNative, scanStrich, scanZxing])

  return {
    platform,
    supported: platform !== 'unsupported',
    ready,
    scanning,
    scan,
    stop,
  }
}
